import { useRef, useState } from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'

/**
 * Cancel with no unsaved changes navigates immediately and invokes no command.
 * Cancel or any router navigation attempt with unsaved changes goes through
 * this warning boundary first — `useBlocker`, verified present in the
 * installed react-router-dom v7 (a data-router hook), is the actual supported
 * navigation-blocking mechanism, not an assumed one.
 *
 * `bypassRef` lets a confirmed discard's own `navigate()` call through: without
 * it, that very call would immediately re-trigger the same blocker, since
 * `isDirty` is still true at the instant it runs (a ref, not state, is read
 * synchronously by `useBlocker`'s predicate within the same tick).
 */
export function useUnsavedNavigationGuard(isDirty: boolean) {
	const navigate = useNavigate()
	const bypassRef = useRef(false)
	const blocker = useBlocker(() => isDirty && !bypassRef.current)
	const [pendingCancelPath, setPendingCancelPath] = useState<string | null>(null)

	function onCancel(path: string) {
		if (!isDirty) {
			void navigate(path)
			return
		}
		setPendingCancelPath(path)
	}

	function onDiscard() {
		bypassRef.current = true
		if (blocker.state === 'blocked') blocker.proceed()
		if (pendingCancelPath) {
			const path = pendingCancelPath
			setPendingCancelPath(null)
			void navigate(path)
		}
	}

	function onKeepEditing() {
		if (blocker.state === 'blocked') blocker.reset()
		setPendingCancelPath(null)
	}

	/** For a successful save's own navigation — bypasses the guard even though the form is still technically dirty. */
	function navigateAfterSave(path: string) {
		bypassRef.current = true
		void navigate(path)
	}

	return {
		isWarningOpen: blocker.state === 'blocked' || pendingCancelPath !== null,
		onCancel,
		onDiscard,
		onKeepEditing,
		navigateAfterSave,
	}
}
